import { cn } from '@/lib/utils'
import { useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react'

// ========== TYPES ==========

interface OutlineSlide {
  id: string
  order: number
  status: 'created' | 'updated' | 'deleted' | 'reordered'
  title?: string
  layout?: string
  fit?: 'perfect' | 'adapted' | 'stretched'
  adaptation?: string
  reason?: string
  summary?: string
  previousOrder?: number
}

interface OutlineCorrection {
  slide: string
  change: string
}

interface OutlineResult {
  status: 'success' | 'partial' | 'failed'
  action: 'create' | 'update'
  title: string
  slideCount: number
  storyArc?: string
  slides?: OutlineSlide[]  // For CREATE mode
  changes?: OutlineSlide[] // For EDIT mode
  validation: 'passed' | 'corrected' | 'failed'
  corrections?: OutlineCorrection[]
  dataSource?: string
  summary: string
}

interface OutlineError {
  status: 'error'
  code: string
  reason: string
  recovery?: string
}

// ========== PARSING ==========

/**
 * Parse outline_summary JSON from message content.
 * Handles both: <outline_summary>...</outline_summary> AND bare outline_summary.../outline_summary
 */
export function parseOutlineSummary(content: string): {
  hasOutlineResult: boolean
  result?: OutlineResult
  remainingContent?: string
} {
  // Try XML-style first, then bare tags (model sometimes omits angle brackets)
  const match = content.match(/<outline_summary>([\s\S]*?)<\/outline_summary>/)
    || content.match(/(?:^|\n)outline_summary\n([\s\S]*?)\n\/outline_summary/m)
  if (!match) {
    return { hasOutlineResult: false }
  }

  try {
    const json = JSON.parse(match[1].trim()) as {
      action?: string
      slideCount?: number
      slides?: Array<{
        id: string
        title?: string
        layout?: string
        status?: string
        order?: number
        summary?: string
        reason?: string
        previousOrder?: number
      }>
      validationStatus?: string
      adaptationsApplied?: string[]
      summary?: string
      storyArc?: string
      title?: string
    }

    // Map all slides from JSON (used for both CREATE and EDIT)
    const slides: OutlineSlide[] = (json.slides || []).map((s, i) => ({
      id: s.id,
      order: s.order ?? i + 1,
      status: (s.status as OutlineSlide['status']) || 'created',
      title: s.title,
      layout: s.layout,
      summary: s.summary || s.reason,
      previousOrder: s.previousOrder,
    }))

    // Build adaptations summary
    const adaptationSummary = json.adaptationsApplied?.length
      ? `${json.adaptationsApplied.length} layout adaptations applied`
      : undefined

    const result: OutlineResult = {
      status: json.validationStatus === 'passed' ? 'success' :
              json.validationStatus === 'failed' ? 'failed' : 'success',
      action: (json.action as OutlineResult['action']) || 'create',
      title: json.title || slides[0]?.title || 'Outline',
      slideCount: json.slideCount || slides.length,
      storyArc: json.storyArc,
      // For create mode, all slides go in `slides`. For edit mode, changed slides go in `changes`.
      slides: json.action !== 'update' && slides.length > 0 ? slides : undefined,
      changes: json.action === 'update' && slides.length > 0 ? slides : undefined,
      validation: (json.validationStatus as OutlineResult['validation']) || 'passed',
      corrections: json.adaptationsApplied?.map(a => {
        const slideMatch = a.match(/on (slide-\d+)/)
        return {
          slide: slideMatch?.[1] || 'unknown',
          change: a,
        }
      }),
      summary: json.summary || adaptationSummary || `${json.action === 'update' ? 'Updated' : 'Created'} ${json.slideCount || slides.length} slides`,
    }

    const remainingContent = content
      .replace(/<outline_summary>[\s\S]*?<\/outline_summary>\s*/g, '')
      .replace(/(?:^|\n)outline_summary\n[\s\S]*?\n\/outline_summary\s*/gm, '')
      .trim()

    return {
      hasOutlineResult: true,
      result,
      remainingContent: remainingContent || undefined,
    }
  } catch {
    return { hasOutlineResult: false }
  }
}

/**
 * Parse <outline_result> XML from message content
 */
export function parseOutlineResult(content: string): {
  hasOutlineResult: boolean
  result?: OutlineResult
  remainingContent?: string
} {
  const match = content.match(/<outline_result>([\s\S]*?)<\/outline_result>/)
  if (!match) {
    return { hasOutlineResult: false }
  }

  try {
    const xml = match[1]

    // Parse individual fields from XML
    const status = xml.match(/<status>(.*?)<\/status>/)?.[1] as OutlineResult['status']
    const action = xml.match(/<action>(.*?)<\/action>/)?.[1] as OutlineResult['action']
    const title = xml.match(/<title>(.*?)<\/title>/)?.[1] || ''
    const slideCount = parseInt(xml.match(/<slideCount>(.*?)<\/slideCount>/)?.[1] || '0')
    const storyArc = xml.match(/<storyArc>(.*?)<\/storyArc>/)?.[1]
    const validation = xml.match(/<validation>(.*?)<\/validation>/)?.[1] as OutlineResult['validation']
    const dataSource = xml.match(/<dataSource>(.*?)<\/dataSource>/)?.[1]
    const summary = xml.match(/<summary>(.*?)<\/summary>/)?.[1] || ''

    // Parse slides (CREATE mode)
    const slides: OutlineSlide[] = []
    const slidesMatch = xml.match(/<slides>([\s\S]*?)<\/slides>/)
    if (slidesMatch) {
      const slideRegex = /<slide id="([^"]+)" order="(\d+)" status="([^"]+)">([\s\S]*?)<\/slide>/g
      let slideMatch
      while ((slideMatch = slideRegex.exec(slidesMatch[1])) !== null) {
        const slideContent = slideMatch[4]
        slides.push({
          id: slideMatch[1],
          order: parseInt(slideMatch[2]),
          status: slideMatch[3] as OutlineSlide['status'],
          title: slideContent.match(/<title>(.*?)<\/title>/)?.[1],
          layout: slideContent.match(/<layout>(.*?)<\/layout>/)?.[1],
          fit: slideContent.match(/<fit>(.*?)<\/fit>/)?.[1] as OutlineSlide['fit'],
          adaptation: slideContent.match(/<adaptation>(.*?)<\/adaptation>/)?.[1],
        })
      }
    }

    // Parse changes (EDIT mode)
    const changes: OutlineSlide[] = []
    const changesMatch = xml.match(/<changes>([\s\S]*?)<\/changes>/)
    if (changesMatch) {
      const changeRegex = /<slide id="([^"]+)" order="(\d+)" status="([^"]+)">([\s\S]*?)<\/slide>/g
      let changeMatch
      while ((changeMatch = changeRegex.exec(changesMatch[1])) !== null) {
        const changeContent = changeMatch[4]
        changes.push({
          id: changeMatch[1],
          order: parseInt(changeMatch[2]),
          status: changeMatch[3] as OutlineSlide['status'],
          title: changeContent.match(/<title>(.*?)<\/title>/)?.[1],
          layout: changeContent.match(/<layout>(.*?)<\/layout>/)?.[1],
          fit: changeContent.match(/<fit>(.*?)<\/fit>/)?.[1] as OutlineSlide['fit'],
          adaptation: changeContent.match(/<adaptation>(.*?)<\/adaptation>/)?.[1],
          reason: changeContent.match(/<reason>(.*?)<\/reason>/)?.[1],
          previousOrder: parseInt(changeContent.match(/<previousOrder>(.*?)<\/previousOrder>/)?.[1] || '0') || undefined,
        })
      }
    }

    // Parse corrections
    const corrections: OutlineCorrection[] = []
    const correctionsMatch = xml.match(/<corrections>([\s\S]*?)<\/corrections>/)
    if (correctionsMatch) {
      const correctionRegex = /<correction slide="([^"]+)">(.*?)<\/correction>/g
      let corrMatch
      while ((corrMatch = correctionRegex.exec(correctionsMatch[1])) !== null) {
        corrections.push({
          slide: corrMatch[1],
          change: corrMatch[2],
        })
      }
    }

    const result: OutlineResult = {
      status,
      action,
      title,
      slideCount,
      storyArc,
      slides: slides.length > 0 ? slides : undefined,
      changes: changes.length > 0 ? changes : undefined,
      validation,
      corrections: corrections.length > 0 ? corrections : undefined,
      dataSource,
      summary,
    }

    const remainingContent = content
      .replace(/<outline_result>[\s\S]*?<\/outline_result>\s*/g, '')
      .trim()

    return {
      hasOutlineResult: true,
      result,
      remainingContent: remainingContent || undefined,
    }
  } catch {
    return { hasOutlineResult: false }
  }
}

/**
 * Parse outline_error from message content.
 * Handles both XML-style and bare tags, and both XML-inner and JSON-inner formats.
 */
export function parseOutlineError(content: string): {
  hasOutlineError: boolean
  error?: OutlineError
  remainingContent?: string
} {
  // Try XML-style first, then bare tags
  const match = content.match(/<outline_error>([\s\S]*?)<\/outline_error>/)
    || content.match(/(?:^|\n)outline_error\n([\s\S]*?)\n\/outline_error/m)
  if (!match) {
    return { hasOutlineError: false }
  }

  try {
    const inner = match[1].trim()
    let error: OutlineError

    // Try JSON parse first (new format), fallback to XML field parsing
    if (inner.startsWith('{')) {
      const json = JSON.parse(inner) as { code?: string; reason?: string; recovery?: string }
      error = {
        status: 'error',
        code: json.code || 'UNKNOWN',
        reason: json.reason || 'Unknown error',
        recovery: json.recovery,
      }
    } else {
      error = {
        status: 'error',
        code: inner.match(/<code>(.*?)<\/code>/)?.[1] || 'UNKNOWN',
        reason: inner.match(/<reason>(.*?)<\/reason>/)?.[1] || 'Unknown error',
        recovery: inner.match(/<recovery>(.*?)<\/recovery>/)?.[1],
      }
    }

    const remainingContent = content
      .replace(/<outline_error>[\s\S]*?<\/outline_error>\s*/g, '')
      .replace(/(?:^|\n)outline_error\n[\s\S]*?\n\/outline_error\s*/gm, '')
      .trim()

    return {
      hasOutlineError: true,
      error,
      remainingContent: remainingContent || undefined,
    }
  } catch {
    return { hasOutlineError: false }
  }
}

// ========== DISPLAY COMPONENTS ==========

const statusPrefix: Record<string, string> = {
  created: '+',
  updated: '~',
  deleted: '-',
  reordered: '↕',
}

const statusColor = 'text-muted-foreground'

/**
 * Compact inline outline result - slim chip with expandable slide list
 */
export const OutlineResultDisplay: React.FC<{ result: OutlineResult }> = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const isCreate = result.action === 'create'
  const slideList = isCreate ? result.slides : result.changes

  return (
    <div className="mt-2">
      {/* Inline chip header */}
      <button
        onClick={() => slideList?.length ? setIsExpanded(!isExpanded) : undefined}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all text-xs",
          slideList?.length ? "cursor-pointer" : "cursor-default",
          "bg-muted border-border hover:border-foreground/20"
        )}
      >
        {/* Status dot */}
        {result.status === 'success' ? (
          <Check className="h-3 w-3 text-emerald-500" />
        ) : result.status === 'partial' ? (
          <AlertTriangle className="h-3 w-3 text-amber-500" />
        ) : (
          <AlertCircle className="h-3 w-3 text-red-500" />
        )}

        {/* Action label */}
        <span className="font-medium text-foreground">
          {isCreate ? 'Created' : 'Updated'}
        </span>

        <span className="text-muted-foreground">·</span>

        {/* Slide count */}
        <span className="text-muted-foreground">
          {result.slideCount} slides
        </span>

        <span className="text-muted-foreground">·</span>

        {/* Validation */}
        <span className={cn(
          result.validation === 'passed' && "text-emerald-600 dark:text-emerald-400",
          result.validation === 'corrected' && "text-amber-600 dark:text-amber-400",
          result.validation === 'failed' && "text-red-600 dark:text-red-400"
        )}>
          {result.validation}
        </span>

        {/* Expand chevron */}
        {slideList && slideList.length > 0 && (
          isExpanded
            ? <ChevronDown className="h-3 w-3 text-muted-foreground ml-0.5" />
            : <ChevronRight className="h-3 w-3 text-muted-foreground ml-0.5" />
        )}
      </button>

      {/* Expanded: compact slide list */}
      {isExpanded && slideList && slideList.length > 0 && (
        <div className="mt-1.5 ml-1 pl-3 border-l-2 border-border space-y-0.5 max-h-[320px] overflow-y-auto">
          {slideList.map((slide) => (
            <div key={slide.id} className="text-xs leading-relaxed">
              <div className="flex items-start gap-1.5">
                {/* Status prefix */}
                <span className={cn("font-mono w-3 shrink-0 text-center", statusColor)}>
                  {statusPrefix[slide.status] || '·'}
                </span>

                {/* Order number */}
                <span className="font-mono text-gray-400 dark:text-gray-500 w-4 shrink-0 text-right">
                  {String(slide.order).padStart(2, '0')}
                </span>

                {/* Title */}
                {slide.title ? (
                  <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-300 line-clamp-2">
                    {slide.title}
                  </span>
                ) : slide.status === 'reordered' && slide.previousOrder ? (
                  <span className="text-gray-500 dark:text-gray-400 italic">
                    moved #{slide.previousOrder} → #{slide.order}
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500 italic">{slide.id}</span>
                )}

                {/* Layout tag - only show for non-reordered slides */}
                {slide.layout && slide.status !== 'reordered' && (
                  <span className="text-gray-400 dark:text-gray-600 ml-auto shrink-0">
                    {slide.layout}
                  </span>
                )}
              </div>

              {/* Per-slide summary (edit mode - what changed) */}
              {slide.summary && (
                <div className="ml-[2.125rem] text-gray-500 dark:text-gray-400 italic line-clamp-2">
                  {slide.summary}
                </div>
              )}
            </div>
          ))}

          {/* Adaptations count if any corrections */}
          {result.corrections && result.corrections.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 pt-1">
              <AlertTriangle className="h-3 w-3" />
              <span>{result.corrections.length} layout adaptation{result.corrections.length !== 1 ? 's' : ''} applied</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Compact inline outline error
 */
export const OutlineErrorDisplay: React.FC<{ error: OutlineError }> = ({ error }) => {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-200/60 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-xs">
      <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
      <span className="font-mono text-red-600 dark:text-red-400">{error.code}</span>
      <span className="text-gray-400">·</span>
      <span className="text-red-600 dark:text-red-300">{error.reason}</span>
      {error.recovery && (
        <>
          <span className="text-gray-400">·</span>
          <span className="text-red-500 dark:text-red-400">{error.recovery}</span>
        </>
      )}
    </div>
  )
}
